import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../services/api/apiClient';
import { useAuth } from './AuthContext';

const ThreadsInsightsContext = createContext();

export const useThreadsInsights = () => {
  const context = useContext(ThreadsInsightsContext);
  if (!context) {
    throw new Error('useThreadsInsights must be used within a ThreadsInsightsProvider');
  }
  return context;
};

export const ThreadsInsightsProvider = ({ children }) => {
  // 支持多帳號：key 是 accountId，value 是 insights 數據
  const [accountsInsights, setAccountsInsights] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState(new Set());
  const [fetchingAccounts, setFetchingAccounts] = useState(new Set()); // 正在背景獲取的帳號
  const [errors, setErrors] = useState({});
  const { isAuthenticated } = useAuth();

  // 從 localStorage 讀取所有帳號的快取數據
  useEffect(() => {
    if (isAuthenticated) {
      const cachedData = localStorage.getItem('accountsInsights');
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          console.log(' Loaded cached insights for', Object.keys(parsed).length, 'accounts');
          console.log(' Cached account IDs:', Object.keys(parsed));
          setAccountsInsights(parsed);
        } catch (err) {
          console.error('Failed to parse cached insights:', err);
          localStorage.removeItem('accountsInsights');
        }
      }
    } else {
      // 未認證時清除所有數據
      setAccountsInsights({});
      setErrors({});
      localStorage.removeItem('accountsInsights');
    }
  }, [isAuthenticated]);

  // 保存數據到 localStorage
  const persistToStorage = (insights) => {
    try {
      localStorage.setItem('accountsInsights', JSON.stringify(insights));
    } catch (err) {
      console.error('Failed to persist insights to storage:', err);
    }
  };

  // 載入特定帳號的 insights（只從 Supabase 讀取，每日 00:00 由後端自動更新）
  const fetchAccountInsights = async (accountId, forceRefresh = false, timeRange = 'today') => {
    if (!accountId) return null;

    // 使用 accountId + timeRange 作為快取 key
    const cacheKey = `${accountId}_${timeRange}`;
    if (loadingAccounts.has(cacheKey) && !forceRefresh) {
      return accountsInsights[cacheKey] || null;
    }

    // 檢查 localStorage 快取是否過期（30分鐘）
    const CACHE_DURATION = 30 * 60 * 1000; // 30分鐘
    const cachedData = accountsInsights[cacheKey];

    if (cachedData && !forceRefresh) {
      const cacheAge = Date.now() - (cachedData._fetchedAt || 0);
      if (cacheAge < CACHE_DURATION) {
        const remainingMinutes = Math.round((CACHE_DURATION - cacheAge) / 60000);
        console.log(`⚡ Using localStorage cache for account ${accountId} (${timeRange}) - expires in ${remainingMinutes} minutes`);
        return cachedData;
      } else {
        console.log(` localStorage cache expired for account ${accountId} (${timeRange}), fetching from Supabase...`);
      }
    }

    try {
      setLoadingAccounts(prev => new Set(prev).add(cacheKey));
      setErrors(prev => ({ ...prev, [cacheKey]: null }));

      const token = localStorage.getItem('token');

      // 從 Supabase 讀取已存儲的數據（帶 timeRange 參數）
      console.log(` Loading insights from Supabase for account ${accountId} (${timeRange})...`);

      const response = await apiClient.get(
        `/threads/insights/user-insights?accountId=${accountId}&timeRange=${timeRange}`
      );

      if (response.data.success && response.data.data) {
        const insights = {
          ...accountsInsights,
          [cacheKey]: {
            ...response.data.data,
            _fetchedAt: Date.now(),
            _fromSupabase: true
          }
        };

        setAccountsInsights(insights);
        persistToStorage(insights);

        console.log(`✅ Loaded insights for ${response.data.data.account_name} (${timeRange}) from Supabase`);
        return insights[cacheKey];
      } else {
        console.log(`⚠️ No insights data found for account ${accountId} (${timeRange}) - Fetching from Threads API...`);

        // 標記為正在獲取中
        setFetchingAccounts(prev => new Set(prev).add(cacheKey));

        // 🔥 直接調用 /user-insights 從 Threads API 獲取數據
        try {
          const fetchResponse = await apiClient.get(
            `/threads/insights/user-insights?accountId=${accountId}&timeRange=${timeRange}`
          );

          if (fetchResponse.data.success && fetchResponse.data.data) {
            const insights = {
              ...accountsInsights,
              [cacheKey]: {
                ...fetchResponse.data.data,
                _fetchedAt: Date.now(),
                _fromThreadsAPI: true
              }
            };

            setAccountsInsights(insights);
            persistToStorage(insights);

            console.log(`✅ Fetched fresh insights for ${fetchResponse.data.data.account_name} (${timeRange}) from Threads API`);

            // 清除 fetching 狀態
            setFetchingAccounts(prev => {
              const newSet = new Set(prev);
              newSet.delete(cacheKey);
              return newSet;
            });

            return insights[cacheKey];
          } else {
            throw new Error('No data returned from Threads API');
          }
        } catch (fetchErr) {
          console.error(`❌ Failed to fetch insights from Threads API:`, fetchErr);
          // 清除 fetching 狀態
          setFetchingAccounts(prev => {
            const newSet = new Set(prev);
            newSet.delete(cacheKey);
            return newSet;
          });
          return null;
        }
      }

    } catch (err) {
      console.error(`❌ Failed to load insights for account ${accountId} (${timeRange}):`, err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch data';
      setErrors(prev => ({ ...prev, [cacheKey]: errorMsg }));
      return null;
    } finally {
      setLoadingAccounts(prev => {
        const newSet = new Set(prev);
        newSet.delete(cacheKey);
        return newSet;
      });
    }
  };

  // 預載入所有帳號的 insights（用於登入後）
  const preloadAllInsights = async (accounts) => {
    if (!accounts || accounts.length === 0) return;

    console.log(`🚀 Preloading insights for ${accounts.length} accounts...`);

    // 並行載入所有帳號的數據
    const promises = accounts
      .filter(acc => acc.platform === 'threads' && acc.status === 'active')
      .map(acc => fetchAccountInsights(acc.id, false));

    try {
      await Promise.all(promises);
      console.log('✅ All accounts insights preloaded');
    } catch (err) {
      console.error('⚠️ Some accounts failed to preload:', err);
    }
  };

  // 獲取特定帳號的 insights（如果沒有則返回 null）
  const getAccountInsights = (accountId, timeRange = 'today') => {
    const cacheKey = `${accountId}_${timeRange}`;
    return accountsInsights[cacheKey] || null;
  };

  // 檢查特定帳號是否正在載入
  const isAccountLoading = (accountId, timeRange = 'today') => {
    const cacheKey = `${accountId}_${timeRange}`;
    return loadingAccounts.has(cacheKey);
  };

  // 檢查特定帳號是否正在背景獲取
  const isAccountFetching = (accountId, timeRange = 'today') => {
    const cacheKey = `${accountId}_${timeRange}`;
    return fetchingAccounts.has(cacheKey);
  };

  // 獲取特定帳號的錯誤
  const getAccountError = (accountId, timeRange = 'today') => {
    const cacheKey = `${accountId}_${timeRange}`;
    return errors[cacheKey] || null;
  };

  // 清除所有快取
  const clearAllCache = () => {
    setAccountsInsights({});
    setErrors({});
    setLoadingAccounts(new Set());
    localStorage.removeItem('accountsInsights');
    console.log('🗑️ All insights cache cleared');
  };

  // 清除特定帳號的快取（可以指定 timeRange，或清除該帳號所有 timeRange）
  const clearAccountCache = (accountId, timeRange = null) => {
    const newInsights = { ...accountsInsights };
    const newErrors = { ...errors };

    if (timeRange) {
      // 清除特定 time range
      const cacheKey = `${accountId}_${timeRange}`;
      delete newInsights[cacheKey];
      delete newErrors[cacheKey];
      console.log(`🗑️ Cache cleared for account ${accountId} (${timeRange})`);
    } else {
      // 清除該帳號所有 time ranges
      const timeRanges = ['today', '7days', '30days'];
      timeRanges.forEach(range => {
        const cacheKey = `${accountId}_${range}`;
        delete newInsights[cacheKey];
        delete newErrors[cacheKey];
      });
      console.log(`🗑️ Cache cleared for account ${accountId} (all time ranges)`);
    }

    setAccountsInsights(newInsights);
    persistToStorage(newInsights);
    setErrors(newErrors);
  };

  // 強制更新 insights（呼叫後端 API 立即從 Threads 抓取最新資料）
  const forceUpdateInsights = async (accountId = null, timeRange = null) => {
    try {
      const token = localStorage.getItem('token');

      const rangeStr = timeRange ? ` (${timeRange})` : ' (all time ranges)';
      console.log(`🔄 [Force Update] Triggering manual update${accountId ? ` for account ${accountId}` : ' for all accounts'}${rangeStr}...`);

      // 設置 loading 狀態
      if (accountId && timeRange) {
        const cacheKey = `${accountId}_${timeRange}`;
        setLoadingAccounts(prev => new Set(prev).add(cacheKey));
      } else if (accountId) {
        // 更新該帳號的所有 time ranges
        const timeRanges = ['today', '7days', '30days'];
        const cacheKeys = timeRanges.map(range => `${accountId}_${range}`);
        setLoadingAccounts(prev => new Set([...prev, ...cacheKeys]));
      } else {
        // 如果是更新所有帳號，設置所有帳號的所有 time ranges 為 loading
        const allCacheKeys = Object.keys(accountsInsights);
        setLoadingAccounts(new Set(allCacheKeys));
      }

      const response = await apiClient.post(
        '/threads/insights/force-update',
        { accountId, timeRange }
      );

      if (response.data.success) {
        console.log(`✅ [Force Update] ${response.data.message}`);

        // 清除相關的 localStorage 快取
        if (accountId) {
          clearAccountCache(accountId, timeRange);
        } else {
          clearAllCache();
        }

        // 重新載入 insights
        if (accountId && timeRange) {
          // 更新特定帳號的特定 time range
          await fetchAccountInsights(accountId, true, timeRange);
        } else if (accountId) {
          // 更新特定帳號的所有 time ranges
          const timeRanges = ['today', '7days', '30days'];
          await Promise.all(timeRanges.map(range => fetchAccountInsights(accountId, true, range)));
        } else {
          // 更新所有帳號（需要從 accountsInsights 提取 accountId）
          const uniqueAccountIds = [...new Set(
            Object.keys(accountsInsights).map(key => key.split('_')[0])
          )];
          const timeRanges = ['today', '7days', '30days'];
          const promises = uniqueAccountIds.flatMap(id =>
            timeRanges.map(range => fetchAccountInsights(id, true, range))
          );
          await Promise.all(promises);
        }

        return response.data;
      } else {
        throw new Error(response.data.message || 'Update failed');
      }
    } catch (err) {
      console.error('[Force Update] Error:', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to force update';

      if (accountId && timeRange) {
        const cacheKey = `${accountId}_${timeRange}`;
        setErrors(prev => ({ ...prev, [cacheKey]: errorMsg }));
      } else if (accountId) {
        const timeRanges = ['today', '7days', '30days'];
        const errorUpdates = {};
        timeRanges.forEach(range => {
          errorUpdates[`${accountId}_${range}`] = errorMsg;
        });
        setErrors(prev => ({ ...prev, ...errorUpdates }));
      }

      throw err;
    } finally {
      // 清除 loading 狀態
      if (accountId && timeRange) {
        const cacheKey = `${accountId}_${timeRange}`;
        setLoadingAccounts(prev => {
          const newSet = new Set(prev);
          newSet.delete(cacheKey);
          return newSet;
        });
      } else if (accountId) {
        const timeRanges = ['today', '7days', '30days'];
        const cacheKeys = timeRanges.map(range => `${accountId}_${range}`);
        setLoadingAccounts(prev => {
          const newSet = new Set(prev);
          cacheKeys.forEach(key => newSet.delete(key));
          return newSet;
        });
      } else {
        setLoadingAccounts(new Set());
      }
    }
  };

  const value = {
    // 多帳號支持
    accountsInsights,
    fetchAccountInsights,
    preloadAllInsights,
    getAccountInsights,
    isAccountLoading,
    isAccountFetching, // 新增：檢查背景獲取狀態
    getAccountError,
    clearAccountCache,
    clearAllCache,
    forceUpdateInsights, // 新增：強制更新功能

    // 向後相容（保留舊的單帳號 API）
    loading: loadingAccounts.size > 0,
    error: Object.values(errors)[0] || null,
  };

  return (
    <ThreadsInsightsContext.Provider value={value}>
      {children}
    </ThreadsInsightsContext.Provider>
  );
};